import {
  createUploadSessionApi,
  generatePartUrlsApi,
  uploadPartChunkApi,
  completeUploadSessionApi,
} from '../api/upload';

export const DEFAULT_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Functional Multipart Upload Handler
 * Manages chunking, URLs, progress reporting, retries, and cancellation via closures.
 */
export const uploadMultipartFile = ({
  file,
  uploadSource = 'post', // 'chat' | 'post' | 'disk'
  chunkSize = DEFAULT_CHUNK_SIZE,
  maxConcurrent = 2,
  maxRetries = 3,
  onProgress,
  onStatusChange,
  onError,
  onSuccess,
}) => {
  let uploadId = null;
  let status = 'idle'; // 'idle' | 'initiating' | 'uploading' | 'completing' | 'completed' | 'error' | 'cancelled'
  const abortController = new AbortController();
  const partProgressMap = {};

  const setStatus = (newStatus) => {
    status = newStatus;
    if (onStatusChange) {
      onStatusChange(newStatus);
    }
  };

  const reportOverallProgress = () => {
    if (!onProgress || !file) return;

    let loadedBytes = 0;
    Object.values(partProgressMap).forEach((bytes) => {
      loadedBytes += bytes;
    });

    const totalBytes = file.size;
    const percentage =
      totalBytes > 0 ? Math.min(Math.round((loadedBytes / totalBytes) * 100), 99) : 0;

    onProgress({
      loaded: loadedBytes,
      total: totalBytes,
      percentage,
      uploadId,
    });
  };

  const uploadSinglePartWithRetry = async (singlePartWithPresignedUrl, totalSize) => {
    const partNumber = singlePartWithPresignedUrl.part_number;
    const url = singlePartWithPresignedUrl.url;
    
    const start = (partNumber - 1) * chunkSize;
    const end = Math.min(partNumber * chunkSize, totalSize);
    const chunk = file.slice(start, end);

    let attempt = 0;
    let lastError = null;

    while (attempt < maxRetries) {
      if (abortController.signal.aborted) {
        throw new Error(`Part ${partNumber} upload aborted`);
      }

      try {
        attempt++;
        
        const res = await uploadPartChunkApi({
          url,
          chunk,
          partNumber,
          signal: abortController.signal,
          onProgress: ({ loaded }) => {
            partProgressMap[partNumber] = loaded;
            reportOverallProgress();
          },
        });
        
        return res;
      } catch (err) {
        lastError = err;
        
        if (abortController.signal.aborted) {
          throw err;
        }
        
        if (attempt < maxRetries) {
          await new Promise((r) => setTimeout(r, 1000 * attempt));
        }
      }
    }

    throw lastError || new Error(`Part ${partNumber} failed after ${maxRetries} attempts`);
  };

  const processPartsQueue = async (partsWithPresignedUrls, totalSize) => {
    const results = [];
    const queue = [...partsWithPresignedUrls];
    let activeWorkerCount = 0;

    return new Promise((resolve, reject) => {
      const dispatchNext = () => {
        if (abortController.signal.aborted) {
          return reject(new Error('Upload cancelled'));
        }

        if (queue.length === 0 && activeWorkerCount === 0) {
          return resolve(results);
        }

        while (activeWorkerCount < maxConcurrent && queue.length > 0) {
          const singlePart = queue.shift();
          activeWorkerCount++;

          uploadSinglePartWithRetry(singlePart, totalSize)
            .then((partResult) => {
              results.push(partResult);
              activeWorkerCount--;
              dispatchNext();
            })
            .catch((err) => {
              activeWorkerCount--;
              reject(err);
            });
        }
      };

      dispatchNext();
    });
  };

  const cancel = () => {
    abortController.abort();
    setStatus('cancelled');
  };

  const start = async () => {
    if (!file) {
      const err = new Error('No file provided for upload');
      setStatus('error');
      if (onError) onError(err);
      throw err;
    }

    try {
      setStatus('initiating');

      const totalSize = file.size;
      const totalParts = Math.max(1, Math.ceil(totalSize / chunkSize));
      const partNumbers = Array.from({ length: totalParts }, (_, i) => i + 1);

      // 1. Create Upload Session
      const sessionRes = await createUploadSessionApi({
        upload_source: uploadSource,
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        total_size: totalSize,
        chunk_size: chunkSize,
      });

      uploadId = sessionRes?.data?.upload_id || sessionRes?.upload_id;
      if (!uploadId) {
        throw new Error('Upload session creation did not return an upload_id');
      }

      // 2. Generate Upload URLs for all parts
      const partsRes = await generatePartUrlsApi({
        uploadId,
        partNumbers,
      });

      console.log(partsRes, 'partsRes: uploadMultipartFile.start')

      const partsWithPresignedUrls = partsRes?.parts || partsRes?.data?.parts || [];
      if (!partsWithPresignedUrls.length) {
        throw new Error('Failed to retrieve presigned upload URLs for parts');
      }

      // 3. Upload parts concurrently
      setStatus('uploading');
      
      const uploadedPartsWithETag = await processPartsQueue(partsWithPresignedUrls, totalSize);

      console.log(uploadedPartsWithETag, 'uploadedPartsWithETag: uploadMultipartFile.start')

      // 4. Complete Upload Session
      setStatus('completing');
      
      const completeRes = await completeUploadSessionApi({
        uploadId,
        parts: uploadedPartsWithETag,
      });

      if (onProgress) {
        onProgress({
          loaded: totalSize,
          total: totalSize,
          percentage: 100,
          uploadId,
        });
      }

      setStatus('completed');
      
      const result = {
        uploadId,
        filename: file.name,
        size: file.size,
        mimeType: file.type,
        data: completeRes,
      };

      if (onSuccess) {
        onSuccess(result);
      }

      return result;
    } catch (err) {
      if (status !== 'cancelled') {
        setStatus('error');
        if (onError) onError(err);
      }
      throw err;
    }
  };

  return {
    start,
    cancel,
    getStatus: () => status,
    getUploadId: () => uploadId,
  };
};
